'use client'

import { Hub } from 'aws-amplify/utils'

import { cognitoMonitoring } from '@/services/auth/cognitoMonitoring.client'
import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { useSessionRuntime } from '@/context/SessionRuntimeContext'
import { useSessionOrchestrator } from '@/hooks/useSessionOrchestrator'
import { invalidateCurrentUserQueries } from '@/services/user/user.cache'
import { isTokenRefreshAuthFailure } from '@/services/auth/authErrorClassifier'

const AuthEventsListener = () => {
  const queryClient = useQueryClient()
  const { currentUserId, impersonatedUserId, sessionScopeKey } =
    useSessionRuntime()
  const { beginLogout } = useSessionOrchestrator()

  useEffect(() => {
    const cancel = Hub.listen('auth', async ({ payload }) => {
      try {
        if (payload.event === 'tokenRefresh') cognitoMonitoring.refreshSignal()
        if (payload.event === 'tokenRefresh_failure')
          cognitoMonitoring.refreshSignal(
            payload.data?.error ?? { name: 'UnknownRefreshError' },
          )
        switch (payload.event) {
          case 'signedIn':
          case 'tokenRefresh':
            await invalidateCurrentUserQueries(queryClient, {
              sessionScopeKey,
              currentUserId,
              impersonatedUserId,
            })
            break
          case 'signedOut':
            await beginLogout('manual', { skipSignOut: true })
            break
          case 'tokenRefresh_failure':
            if (isTokenRefreshAuthFailure(payload.data?.error)) {
              await beginLogout('refresh_failure')
            }
            break
          default:
            break
        }
      } catch (error) {
        console.error('[AuthEventsListener] Hub handler failed:', error)
      }
    })

    return () => cancel()
  }, [
    beginLogout,
    currentUserId,
    impersonatedUserId,
    queryClient,
    sessionScopeKey,
  ])

  return null
}

export default AuthEventsListener
